import React, { useEffect } from 'react';
import flowRight from 'lodash/flowRight';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
    withCustomMetrics,
} from '@zocdoc/frontend-metrics';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { recordAssignmentsOnMount } from '@zocdoc-frontend-common/utils-ab';
import { white } from '@zocdoc-mezzanine/foundations-colors';
import { PINA_BOOKING_COMPLETE_POBOLO } from '../../../config/ab/experiments';
import { usePoboloBookingView } from '../../../config/ab/selector';
import { APPOINTMENT_DETAILS_STRINGS } from '../../pages/AppointmentDetails/constants';
import loadingAnimation from './assets/Loading_no-loop.gif';

/**
 *
 * @param {boolean} shouldShowSurvey - indicates if the user is eligible to take the survey and has not completed it
 * @param {boolean} usePoboloBookingView - indicates the variant of the PINA_BOOKING_COMPLETE_POBOLO AB experiment
 * @returns {JSX.Element}
 * @constructor
 */
export function BaseAnimatedLoadingOverlay({
    shouldShowSurvey,
    usePoboloBookingView,
}) {
    if (!usePoboloBookingView) {
        return (
            <LoadingZeeOverlayWithMetrics shouldShowSurvey={shouldShowSurvey} />
        );
    }

    return <LoadingOverlayWithMetrics />;
}

/**
 *
 * @param {function} sendMetric
 * @param {boolean} shouldShowSurvey
 * @returns {JSX.Element}
 * @constructor
 */
function LoadingZeeOverlay({ sendMetric, shouldShowSurvey }) {
    useEffect(() => {
        sendMetric({
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    return (
        <OverlayHeaderContainer
            data-test="loading-mode"
            title={APPOINTMENT_DETAILS_STRINGS.PageTitle}
            shouldShowSurvey={shouldShowSurvey}
        >
            <ImageWrapper>
                <img alt="Loading" src="/images/zee-long-sync.gif" />
            </ImageWrapper>
            <HeaderText>
                <HeaderTitle data-test="sync-header">
                    {APPOINTMENT_DETAILS_STRINGS.WereBookingYourAppointment}
                </HeaderTitle>
                <HeaderDescription data-test="sync-description">
                    {APPOINTMENT_DETAILS_STRINGS.ThisCouldTakeAMinuteOrTwo}
                </HeaderDescription>
            </HeaderText>
        </OverlayHeaderContainer>
    );
}

/**
 *
 * @param {function} sendMetric
 * @returns {JSX.Element}
 * @constructor
 */
function LoadingOverlay({ sendMetric }) {
    useEffect(() => {
        sendMetric({
            element: 'AnimatedLoadingOverlay',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    return (
        <LoadingContainer>
            <AnimationWrapper>
                <img
                    alt="loading"
                    data-test="loading-image"
                    src={loadingAnimation}
                />
            </AnimationWrapper>
        </LoadingContainer>
    );
}

const LoadingOverlayWithMetrics = withCustomMetrics({
    action: 'Viewed Post Booking Loading Overlay',
    component: 'AnimatedLogo',
    page: 'Booking',
    section: 'Booking',
})(LoadingOverlay);

const LoadingZeeOverlayWithMetrics = withCustomMetrics({
    action: 'Viewed Post Booking Loading Overlay',
    component: 'AnimatedLogo',
    element: 'ZeeLogo',
    page: 'Booking',
    section: 'Booking',
})(LoadingZeeOverlay);

const LoadingContainer = styled.div`
    background-color: ${white};
    padding: 0 16px;

    ${breakpoint.small} {
        padding: 0;
    }
`;

const AnimationWrapper = styled.div`
    margin: 0 auto;
    width: 343px;
`;

const OverlayHeaderContainer = styled.div`
    flex-direction: column;
    width: 100%;
    height: 215px;
    background-color: white;
    padding: 0px 0px 34px 0px;
    display: flex;
    align-items: center;
    text-align: center;

    ${breakpoint.small} {
        text-align: left;
        flex-direction: row;
        width: auto;
    }

    ${p => (p.shouldShowSurvey ? '' : 'height: 100vh')};
`;

const HeaderText = styled.div`
    margin: 26px 0px 0px 12px;
`;

export const HeaderTitle = styled.div`
    font: 20px ${FontFamily.SharpSansBold};
    margin-bottom: 5px;

    ${breakpoint.small} {
        font-size: 24px;
        line-height: 40px;
    }
`;

export const HeaderDescription = styled.div`
    font: 16px ${FontFamily.KievitSlabBook};
    max-width: 400px;

    ${breakpoint.small} {
        font: 24px ${FontFamily.KievitSlabBook};
    }
`;

const ImageWrapper = styled.div`
    height: 155px;
    width: 180px;
`;

const mapStateToProps = state => ({
    usePoboloBookingView: usePoboloBookingView(state),
});

export const AnimatedLoadingOverlay = flowRight(
    connect(mapStateToProps),
    recordAssignmentsOnMount([PINA_BOOKING_COMPLETE_POBOLO])
)(BaseAnimatedLoadingOverlay);
